import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useBillingStore } from '@/stores/billing'
import { useCommonStore } from '@/stores/common'
import { useUsageStore, type UsageResource } from '@/stores/usage'
import type { LimitDialogVariant } from '@/constants/pro'

/**
 * Что именно упёрлось в Pro. 'topic' — клик по платному топику (заголовок собираем из его
 * имени), остальные — исчерпанный лимит ресурса (CLOUDPAYMENTS_INTEGRATION.md §8.1).
 */
export type PaywallVariant = 'topic' | UsageResource

/**
 * Видимость шторки Pro. Отдельный стор, а не флаг в common: рядом встанет
 * stores/billing.ts (CLOUDPAYMENTS_INTEGRATION.md §7), и держать платный флоу вместе
 * с топиками и лоадером не хочется.
 *
 * Адресатов у исчерпанного лимита три, и говорить с ними надо по-разному:
 * - гость — модалка входа (см. openLimitDialog);
 * - PaywallDialog — продажа Pro тому, у кого его нет (Free, неизвестный тариф);
 * - LimitReachedDialog — сообщение Pro-юзеру, что ресурс кончился до продления.
 * Разводит их openLimitDialog — единственная точка развилки в проекте.
 *
 * Четвёртая шторка — SubscriptionSuspendedDialog — стоит сбоку от этой развилки: в неё
 * приходят по статусу подписки (несостоявшееся продление), а не по остатку ресурса,
 * поэтому у неё свой флаг, и решает его не openLimitDialog, а гейт и старт приложения.
 */
export const usePaywallStore = defineStore('paywall', () => {
	const visible = ref(false)
	const variant = ref<PaywallVariant>('topic')
	const topicName = ref('')

	const limitVisible = ref(false)
	const limitResource = ref<LimitDialogVariant>('text')

	const suspendedVisible = ref(false)

	/** Клик по pro-топику: заголовок будет из имени функции. */
	function openForTopic(name = '') {
		topicName.value = name
		variant.value = 'topic'
		visible.value = true
	}

	/** Лимит ресурса исчерпан — клиентским гейтом или ответом 402. */
	function openForLimit(resource: UsageResource) {
		topicName.value = ''
		variant.value = resource
		visible.value = true
	}

	/**
	 * Лимит исчерпан: выбирает шторку по тарифу и вариант по срезу.
	 *
	 * Гостю показываем вход, а не продажу Pro: оплатить подписку без аккаунта он
	 * всё равно не сможет — чекаут сам уводит его в ту же модалку (usePaymentFlow).
	 *
	 * Не-Pro (включая неизвестный тариф) уходит в продажу Pro: показать человеку оффер
	 * безопаснее, чем Pro-шторку с пустыми карточками. Все три ресурса на нуле —
	 * четвёртый макет, независимо от того, какой ресурс упёрся.
	 */
	function openLimitDialog(resource: UsageResource) {
		const usage = useUsageStore()
		if (usage.isGuest) {
			useCommonStore().openLoginModal()
			return
		}
		if (!usage.isPro) {
			openForLimit(resource)
			return
		}
		const limits = usage.limits
		const allEmpty =
			!!limits && limits.text.remaining === 0 && limits.image.remaining === 0 && limits.song.remaining === 0
		limitResource.value = allEmpty ? 'all' : resource
		limitVisible.value = true
	}

	/**
	 * Несостоявшееся продление. Чистый переключатель без запроса среза внутри: шторку
	 * открывают из трёх мест (гейт, старт приложения, возврат во вкладку), и сетевой
	 * побочный эффект здесь означал бы лишний GET /usage на каждом из них.
	 */
	function openSuspended() {
		suspendedVisible.value = true
	}

	function closeSuspended() {
		suspendedVisible.value = false
	}

	/**
	 * Приводит шторку в соответствие со свежим срезом. Нужна там, где решение принимает
	 * не действие человека, а данные: старт приложения и возврат во вкладку (см. App.vue).
	 * Гейт этим не пользуется — он уже знает, что генерацию надо перехватить.
	 *
	 * Незавершённый заказ важнее: тому, у кого списание в процессе, нельзя говорить, что
	 * продление не удалось, — деньги в игре. Тот же приоритет, что в useFeatureGate.
	 * pending_order читаем из среза, а не только isAwaitingPayment: на старте поллер
	 * поднимается асинхронно и к этому моменту может ещё не знать про заказ.
	 */
	function syncSuspended() {
		const usage = useUsageStore()
		if (!usage.isSuspended) {
			closeSuspended()
			return
		}
		if (useBillingStore().isAwaitingPayment || usage.pendingOrder) return
		openSuspended()
	}

	function close() {
		visible.value = false
	}

	function closeLimit() {
		limitVisible.value = false
	}

	return {
		visible,
		variant,
		topicName,
		limitVisible,
		limitResource,
		suspendedVisible,
		openForTopic,
		openForLimit,
		openLimitDialog,
		openSuspended,
		closeSuspended,
		syncSuspended,
		close,
		closeLimit
	}
})
